/**
 * What the PA and UMED workspace headers say about how current they are
 * (2026-10-12, after the Monday sunset).
 *
 * A workspace is as current as eMerge's half-hourly update -- the projection
 * run the server reports as `last_projection_at`. The header says "Updated
 * <that>", and "Not current" is said only when THAT update has stopped
 * (`freshness` BEHIND / STALE / NEVER, measured by the server from the same
 * run, backend/src/services/workspaceFreshness.js).
 *
 * Monday's sync is retired for good (backend/src/services/mondaySunset.js).
 * Its last data is said neutrally, as a fact about a retired source, never as
 * a warning: "Monday board retired — its last data is from Oct 9, 4:11 PM".
 */

export type WorkspaceFreshness = 'FRESH' | 'BEHIND' | 'STALE' | 'NEVER';

export interface FreshnessCounts {
  last_projection_at: string | null;
  freshness: WorkspaceFreshness;
  monday_retired?: boolean;
  monday_final_sync_at?: string | null;
}

/**
 * The day Monday's sync was retired (Isaiah Cotto, 2026-10-11; its last write
 * was 2026-10-09). A constant, as on the server (mondaySunset.js): the sync
 * does not come back.
 */
export const MONDAY_RETIRED_ON = '2026-10-11';

/** An instant in the viewer's own time, as the workspaces' shortDateTime says it ("Oct 9, 4:11 PM"). */
export function instant(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** The header stamp: eMerge's own last update. */
export function updatedStamp(counts: Pick<FreshnessCounts, 'last_projection_at'>): string {
  const at = instant(counts.last_projection_at);
  return at ? `Updated ${at}` : 'Not updated yet';
}

/** Monday's frozen data, said neutrally; null when Monday is not retired or never synced. */
export function mondayRetiredLine(counts: Pick<FreshnessCounts, 'monday_retired' | 'monday_final_sync_at'>): string | null {
  if (!counts.monday_retired) return null;
  const at = instant(counts.monday_final_sync_at);
  return at ? `Monday board retired — its last data is from ${at}` : 'Monday board retired';
}

/** The "Not current" sentence, about eMerge's own update only; null when it is current. */
export function notCurrentText(counts: Pick<FreshnessCounts, 'last_projection_at' | 'freshness'>): string | null {
  if (counts.freshness === 'FRESH') return null;
  const ran = instant(counts.last_projection_at);
  if (counts.freshness === 'NEVER' || !ran) {
    return "eMerge's half-hourly update has not run yet, so nothing here is current. Tell the admin.";
  }
  if (counts.freshness === 'STALE') {
    return `eMerge's half-hourly update last ran ${ran}, more than a day ago; it may be stuck — tell the admin. Work that arrived since is not here yet.`;
  }
  return `eMerge's half-hourly update last ran ${ran}; it may be stuck — tell the admin. Work that arrived since may not be here yet.`;
}
